import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout, theme } from 'antd';
import {
  AppstoreOutlined,
  AuditOutlined,
  CodeOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DollarOutlined,
  GlobalOutlined,
  KeyOutlined,
  MessageOutlined,
  PhoneOutlined,
  RobotOutlined,
  SafetyOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { AppSider, type SiderMenuGroup } from '@/shared/components/AppSider';
import { AppHeader } from '@/shared/components/AppHeader';
import { ImpersonationBanner } from '@/shared/components/ImpersonationBanner';
import type { CommandItem } from '@/shared/components/CommandPalette';

const { Content } = Layout;

const CUSTOMER_GROUPS: SiderMenuGroup[] = [
  {
    title: 'Analyse',
    items: [
      { key: '/dashboard/overview', icon: <DashboardOutlined />, label: 'Übersicht' },
      { key: '/dashboard/sessions', icon: <MessageOutlined />, label: 'Sessions' },
      { key: '/dashboard/spend-report', icon: <AuditOutlined />, label: 'Spend-Report' },
    ],
  },
  {
    title: 'Abrechnung',
    items: [{ key: '/dashboard/credits', icon: <WalletOutlined />, label: 'Guthaben' }],
  },
  {
    title: 'Content & KI',
    items: [
      { key: '/dashboard/datasources', icon: <DatabaseOutlined />, label: 'Datenquellen' },
      { key: '/dashboard/chunk-cleanup', icon: <DeleteOutlined />, label: 'Chunk-Bereinigung' },
      { key: '/dashboard/chat-test', icon: <RobotOutlined />, label: 'Chat testen' },
      { key: '/dashboard/skills', icon: <ThunderboltOutlined />, label: 'Skills' },
    ],
  },
  {
    title: 'Integration',
    items: [{ key: '/dashboard/api-keys', icon: <KeyOutlined />, label: 'API-Keys' }],
  },
  {
    title: 'Konfiguration',
    items: [
      { key: '/dashboard/config/ai', icon: <RobotOutlined />, label: 'KI-Anbieter' },
      { key: '/dashboard/config/model', icon: <ThunderboltOutlined />, label: 'Modell & Prompt' },
      { key: '/dashboard/config/widget', icon: <AppstoreOutlined />, label: 'Widget & Aussehen' },
      { key: '/dashboard/config/handover', icon: <PhoneOutlined />, label: 'Handover' },
      { key: '/dashboard/config/security', icon: <SafetyOutlined />, label: 'Sicherheit' },
      { key: '/dashboard/config/limits', icon: <DollarOutlined />, label: 'Limits & Budget' },
      { key: '/dashboard/config/languages', icon: <GlobalOutlined />, label: 'Sprachen' },
      { key: '/dashboard/config/embed', icon: <CodeOutlined />, label: 'Einbettung' },
    ],
  },
];

const PATH_LABELS: Record<string, string> = {
  dashboard: 'Portal',
  overview: 'Übersicht',
  sessions: 'Sessions',
  datasources: 'Datenquellen',
  'api-keys': 'API-Keys',
  'chat-test': 'Chat testen',
  config: 'Konfiguration',
  ai: 'KI-Anbieter',
  model: 'Modell & Prompt',
  widget: 'Widget & Aussehen',
  handover: 'Handover',
  security: 'Sicherheit',
  limits: 'Limits & Budget',
  languages: 'Sprachen',
  embed: 'Einbettung',
  'spend-report': 'Spend-Report',
  'chunk-cleanup': 'Chunk-Bereinigung',
  credits: 'Guthaben',
  skills: 'Skills',
  topups: 'Aufladung',
  return: 'Zahlungsstatus',
};

const COMMANDS: CommandItem[] = [
  { id: 'nav-overview', label: 'Übersicht', hint: '/dashboard/overview', icon: <DashboardOutlined />, group: 'Navigation', to: '/dashboard/overview', keywords: ['home', 'start', 'dashboard'] },
  { id: 'nav-sessions', label: 'Sessions', hint: '/dashboard/sessions', icon: <MessageOutlined />, group: 'Navigation', to: '/dashboard/sessions', keywords: ['chats', 'gespraeche'] },
  { id: 'nav-datasources', label: 'Datenquellen', hint: '/dashboard/datasources', icon: <DatabaseOutlined />, group: 'Navigation', to: '/dashboard/datasources', keywords: ['inhalte', 'crawl', 'upload'] },
  { id: 'nav-chunk-cleanup', label: 'Chunk-Bereinigung', hint: '/dashboard/chunk-cleanup', icon: <DeleteOutlined />, group: 'Navigation', to: '/dashboard/chunk-cleanup', keywords: ['loeschen', 'cleanup', 'bereinigung', 'impressum'] },
  { id: 'nav-api-keys', label: 'API-Keys', hint: '/dashboard/api-keys', icon: <KeyOutlined />, group: 'Navigation', to: '/dashboard/api-keys', keywords: ['authentifizierung', 'token'] },
  { id: 'nav-chat-test', label: 'Chat testen', hint: '/dashboard/chat-test', icon: <RobotOutlined />, group: 'Navigation', to: '/dashboard/chat-test', keywords: ['preview', 'test', 'vorschau'] },
  { id: 'nav-skills', label: 'Skills', hint: '/dashboard/skills', icon: <ThunderboltOutlined />, group: 'Navigation', to: '/dashboard/skills', keywords: ['agentic', 'skills', 'ki'] },
  { id: 'nav-spend-report', label: 'Spend-Report', hint: '/dashboard/spend-report', icon: <AuditOutlined />, group: 'Navigation', to: '/dashboard/spend-report', keywords: ['kosten', 'abrechnung', 'report', 'embedding'] },
  { id: 'nav-credits', label: 'Guthaben', hint: '/dashboard/credits', icon: <WalletOutlined />, group: 'Navigation', to: '/dashboard/credits', keywords: ['prepaid', 'aufladung', 'wallet', 'topup', 'zahlung'] },
  // Konfiguration-Sub-Seiten
  { id: 'nav-config-ai', label: 'KI-Anbieter', hint: '/dashboard/config/ai', icon: <RobotOutlined />, group: 'Konfiguration', to: '/dashboard/config/ai', keywords: ['provider', 'llm', 'openai', 'anthropic', 'gemini', 'byok', 'embedding'] },
  { id: 'nav-config-model', label: 'Modell & Prompt', hint: '/dashboard/config/model', icon: <ThunderboltOutlined />, group: 'Konfiguration', to: '/dashboard/config/model', keywords: ['modell', 'prompt', 'system-prompt', 'temperatur', 'tokens'] },
  { id: 'nav-config-widget', label: 'Widget & Aussehen', hint: '/dashboard/config/widget', icon: <AppstoreOutlined />, group: 'Konfiguration', to: '/dashboard/config/widget', keywords: ['widget', 'farbe', 'greeting', 'begruessung', 'live', 'design'] },
  { id: 'nav-config-handover', label: 'Handover', hint: '/dashboard/config/handover', icon: <PhoneOutlined />, group: 'Konfiguration', to: '/dashboard/config/handover', keywords: ['live', 'agent', 'uebergabe', 'kontaktformular'] },
  { id: 'nav-config-security', label: 'Sicherheit', hint: '/dashboard/config/security', icon: <SafetyOutlined />, group: 'Konfiguration', to: '/dashboard/config/security', keywords: ['pii', 'datenschutz', 'filter', 'dsgvo'] },
  { id: 'nav-config-limits', label: 'Limits & Budget', hint: '/dashboard/config/limits', icon: <DollarOutlined />, group: 'Konfiguration', to: '/dashboard/config/limits', keywords: ['budget', 'limit', 'rate', 'kosten', 'byok'] },
  { id: 'nav-config-languages', label: 'Sprachen', hint: '/dashboard/config/languages', icon: <GlobalOutlined />, group: 'Konfiguration', to: '/dashboard/config/languages', keywords: ['sprache', 'language', 'fallback', 'mehrsprachig'] },
  { id: 'nav-config-embed', label: 'Einbettung', hint: '/dashboard/config/embed', icon: <CodeOutlined />, group: 'Konfiguration', to: '/dashboard/config/embed', keywords: ['snippet', 'javascript', 'cdn', 'einbetten', 'widget'] },
  { id: 'nav-config', label: 'Konfiguration', hint: '/dashboard/config', icon: <SettingOutlined />, group: 'Konfiguration', to: '/dashboard/config', keywords: ['einstellungen', 'config', 'konfiguration'] },
];

export function CustomerLayout(): React.ReactElement {
  const { token } = theme.useToken();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppSider variant='light' accent='#0ea5e9' logoInitials='KI' title='KI-Dashboard' subtitle='PORTAL' collapsed={collapsed} onCollapseChange={setCollapsed} groups={CUSTOMER_GROUPS} statusLabel='Alle Systeme online' statusVariant='ok' version='v1.0.0' />

      <Layout>
        <ImpersonationBanner />
        <AppHeader collapsed={collapsed} onToggleCollapsed={() => setCollapsed((v) => !v)} commands={COMMANDS} roleLabel='Kunde' accent='#0ea5e9' pathLabels={PATH_LABELS} />

        <Content
          style={{
            padding: 24,
            background: token.colorBgLayout,
            overflowY: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
